// src/components/Control/Features/MicTest.tsx
// Тест микрофона робота (Настройки → Голос и звук): 5 с звука с робота через
// audio/live, уровень по ходу записи, вывод «слышит / тихо / тишина», прослушивание
// записи с автоусилением и программное усиление на роботе (mic_gain).
//   robot/<id>/audio/cmd   <- {"cmd": "listen"} (каждые 2 с), {"cmd": "mic_gain", "value": дБ}
//   robot/<id>/audio/live  -> PCM int16 16 кГц, 100 мс на сообщение
import React, { useEffect, useRef, useState } from 'react';
import { putJson, useZenohSubscription } from '../../../hooks/useZenohSubscription';
import { playClip } from '../../../audio/pcm';
import { useRobot } from '../robot/RobotContext';
import { useAudioState } from './NannyPanel';

const TEST_S = 5;
const NS_LEVELS = ['выключено', 'слабое', 'среднее', 'сильное (рекомендуется)', 'максимальное'];
const MAX_BOOST_DB = 20;  // автоусиление при прослушивании: больше — тишина превращается в громкое шипение
const dbfs = (rms: number) => (rms > 0 ? 20 * Math.log10(rms) : -120);
const pct = (db: number) => Math.max(0, Math.min(100, ((db + 90) / 90) * 100));

interface Result {
  frames: number;
  avgDb: number;
  peakDb: number;
  spreadDb: number;   // разброс уровня по кадрам: речь/шум «гуляют», мёртвый вход — нет
  pcm: Int16Array;
}

function verdict(r: Result): { level: 'ok' | 'warn' | 'alert'; text: string } {
  if (!r.frames) {
    return { level: 'alert', text: 'Робот не прислал звука: микрофон не открылся (см. ошибку выше) или нода audio не работает.' };
  }
  if (r.peakDb < -55 && r.spreadDb < 8) {
    return {
      level: 'alert',
      text: `Тишина на входе: пик ${r.peakDb.toFixed(0)} дБ и уровень почти не меняется — звук до микрофона не доходит. ` +
        'Если это USB-звуковая карта с гнездом 3,5 мм — вставьте микрофон в розовое гнездо (не в зелёное); ' +
        'если USB-микрофон — проверьте его на компьютере. Говорили или хлопали во время теста?',
    };
  }
  if (r.peakDb < -35) {
    return { level: 'warn', text: `Слабый сигнал (пик ${r.peakDb.toFixed(0)} дБ): говорите ближе или добавьте усиление ниже.` };
  }
  if (r.peakDb > -1) {
    return { level: 'warn', text: 'Перегруз (пик у 0 дБ): уменьшите усиление.' };
  }
  return { level: 'ok', text: `Микрофон слышит: средний ${r.avgDb.toFixed(0)} дБ, пик ${r.peakDb.toFixed(0)} дБ.` };
}

const MicTest: React.FC = () => {
  const { session, robotId } = useRobot();
  const prefix = `robot/${robotId}/audio`;
  const { state, online } = useAudioState();
  const [running, setRunning] = useState(false);
  const [left, setLeft] = useState(0);
  const [nowDb, setNowDb] = useState<number | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [gain, setGain] = useState<number | null>(null);   // ползунок, пока двигают
  const chunks = useRef<Int16Array[]>([]);
  const levels = useRef<number[]>([]);
  const stopClip = useRef<(() => void) | null>(null);
  const [playing, setPlaying] = useState(false);

  useZenohSubscription(session, running ? `${prefix}/live` : null, sample => {
    const b = sample.payload().toBytes();
    const pcm = new Int16Array(b.buffer.slice(b.byteOffset, b.byteOffset + (b.byteLength & ~1)));
    if (!pcm.length) return;
    let sum = 0;
    for (let i = 0; i < pcm.length; i++) sum += (pcm[i] / 32768) ** 2;
    const db = dbfs(Math.sqrt(sum / pcm.length));
    chunks.current.push(pcm);
    levels.current.push(db);
    setNowDb(db);
  });

  useEffect(() => {
    if (!running || !session) return;
    chunks.current = [];
    levels.current = [];
    const ping = () => putJson(session, `${prefix}/cmd`, { cmd: 'listen' });
    ping();
    const t = setInterval(ping, 2000);
    const started = Date.now();
    const tick = setInterval(() => {
      const rest = TEST_S - (Date.now() - started) / 1000;
      setLeft(Math.max(0, Math.ceil(rest)));
      if (rest <= 0) setRunning(false);
    }, 200);
    return () => {
      clearInterval(t);
      clearInterval(tick);
      const total = chunks.current.reduce((n, c) => n + c.length, 0);
      const pcm = new Int16Array(total);
      let off = 0;
      let peak = 0;
      let sum = 0;
      for (const c of chunks.current) {
        pcm.set(c, off);
        off += c.length;
        for (let i = 0; i < c.length; i++) {
          const v = Math.abs(c[i]) / 32768;
          peak = Math.max(peak, v);
          sum += v * v;
        }
      }
      // первые кадры после открытия микрофона пропускаем: там бывает щелчок
      const lv = levels.current.slice(2);
      const spread = lv.length ? Math.max(...lv) - Math.min(...lv) : 0;
      setResult({ frames: chunks.current.length, avgDb: dbfs(Math.sqrt(sum / Math.max(1, total))),
                  peakDb: dbfs(peak), spreadDb: spread, pcm });
      setNowDb(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, session, prefix]);

  useEffect(() => () => stopClip.current?.(), []);

  // Запись целиком (все 5 с) с автоусилением до −3 дБ по пику, но не больше MAX_BOOST_DB:
  // тихую речь слышно, а тишина не раздувается в шипение
  const boostDb = result?.pcm.length ? Math.min(MAX_BOOST_DB, Math.max(0, -3 - result.peakDb)) : 0;
  const playback = () => {
    if (!result?.pcm.length) return;
    stopClip.current?.();
    if (playing) {
      setPlaying(false);
      return;
    }
    setPlaying(true);
    stopClip.current = playClip(result.pcm, 10 ** (boostDb / 20), () => { stopClip.current = null; setPlaying(false); });
  };

  const robotGain = gain ?? state?.nanny.mic_gain_db ?? 0;
  const asleep = !!state?.asleep;
  const v = result ? verdict(result) : null;

  return (
    <div className="feature-card">
      <h3>
        <span>🎤 Тест микрофона</span>
        {online ? (
          <span className={`badge ${state?.mic.ok === false ? 'alert' : state?.mic.ok ? 'ok' : ''}`}>
            {state?.mic.ok === false ? 'ошибка' : state?.mic.on ? 'включён' : 'выключен'}
          </span>
        ) : <span className="badge off">нет данных</span>}
      </h3>
      {state?.mic.ok === false && <p className="error-text">Микрофон робота: {state.mic.error}</p>}
      {asleep && <p className="hint">Робот спит: тест недоступен, разбудите его в «Питание и сон».</p>}
      <div className="row">
        <button type="button" className={`op-btn ${running ? 'active' : 'primary'}`} disabled={!session || !online || asleep}
                onClick={() => { setResult(null); setRunning(r => !r); }}>
          {running ? `⏹ Остановить (${left} с)` : `▶ Проверить микрофон (${TEST_S} с)`}
        </button>
        {result && result.pcm.length > 0 && !running && (
          <button type="button" className="op-btn" onClick={playback}
                  title="Запись проигрывается с автоусилением, чтобы было слышно даже тихий сигнал">
            {playing ? '⏹ Остановить' : `🔊 Прослушать запись${boostDb >= 1 ? ` (+${boostDb.toFixed(0)} дБ)` : ''}`}
          </button>
        )}
      </div>
      {running && (
        <>
          <p className="hint">Говорите или хлопните рядом с роботом.</p>
          <div className="row">
            <span>Уровень</span>
            <div className="level-meter" role="meter" aria-label="Уровень звука у робота" aria-valuemin={-90} aria-valuemax={0}
                 aria-valuenow={nowDb ?? -90}>
              {nowDb !== null && <div className="fill" style={{ width: `${pct(nowDb)}%` }} />}
            </div>
            <span className="hint">{nowDb !== null ? `${nowDb.toFixed(0)} дБ` : 'жду звук…'}</span>
          </div>
        </>
      )}
      {v && !running && (
        <p className={v.level === 'ok' ? 'hint' : 'error-text'} role="status">
          {v.level === 'ok' ? '✅ ' : '⚠ '}{v.text}
          {result!.frames > 0 && ` Кадров: ${result!.frames}, разброс ${result!.spreadDb.toFixed(0)} дБ.`}
        </p>
      )}
      <div className="row">
        <label htmlFor="mic-gain">Усиление на роботе</label>
        <input id="mic-gain" type="range" min={0} max={40} step={1} value={robotGain} disabled={!online}
               onChange={e => setGain(Number(e.target.value))}
               onPointerUp={() => { if (gain !== null) { putJson(session, `${prefix}/cmd`, { cmd: 'mic_gain', value: gain }); setGain(null); } }}
               onKeyUp={() => { if (gain !== null) { putJson(session, `${prefix}/cmd`, { cmd: 'mic_gain', value: gain }); setGain(null); } }} />
        <span className="hint">+{robotGain} дБ</span>
      </div>
      <div className="row">
        <label htmlFor="mic-ns">Шумоподавление</label>
        <select id="mic-ns" value={state?.nanny.mic_ns ?? 0} disabled={!online || !state?.denoise}
                onChange={e => putJson(session, `${prefix}/cmd`, { cmd: 'nanny', mic_ns: Number(e.target.value) })}>
          {NS_LEVELS.map((t, i) => <option key={i} value={i}>{t}</option>)}
        </select>
        <label className="op-check">
          <input type="checkbox" checked={!!state?.nanny.mic_agc} disabled={!online || !state?.denoise}
                 onChange={e => putJson(session, `${prefix}/cmd`, { cmd: 'nanny', mic_agc: e.target.checked })} />
          Автоусиление речи
        </label>
      </div>
      {online && state && state.denoise === false && (
        <p className="hint">Шумоподавления нет на роботе: <code>python3 -m pip install --user --break-system-packages webrtc-noise-gain</code></p>
      )}
      <p className="hint">Усиление применяется ко всему: прослушивание, режим няни, записи. Тишину оно не исправит —
        если на входе ноль, усиливать нечего. Шумоподавление убирает ровный фон (вентилятор, гул) из прослушивания,
        записей и распознавания речи — с ним можно поднять усиление, не получая шипения. Автоусиление подтягивает
        тихую речь, но вместе с фоном: на 3 м и дальше распознавание с ним хуже.</p>
    </div>
  );
};

export default MicTest;
